import { useEffect, useRef, useState } from 'react'

import { useAppDispatch } from 'src/store/hooks'
import { showAlert } from 'src/actions/alert.actions'
import { handlerError } from 'src/selectors/error.selector'
import clientsService from 'src/services/clients.service'

export const CLIENTS_PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300

export interface SimpleClient {
  id: number
  code?: string
  companyName: string
  locked?: boolean
  lockMessage?: string
  email?: string
  address?: string
  identifications?: {
    clientId: number
    identificationTypeId: number
    value: string
  }[]
}

interface ClientsPaginatedParams {
  attendant?: number
}

interface PageState {
  items: SimpleClient[]
  skip: number
  hasMore: boolean
  initialized: boolean
}

const emptyPage: PageState = { items: [], skip: 0, hasMore: true, initialized: false }

const mergeById = (prev: SimpleClient[], next: SimpleClient[]): SimpleClient[] => {
  const seen = new Set(prev.map(client => client.id))
  return [...prev, ...next.filter(client => !seen.has(client.id))]
}

/**
 * Estado local de clientes paginados para el selector (sin Redux).
 * Dos modos: browse (lista acumulada por scroll) y search (server-side,
 * también paginada). Limpiar la búsqueda restaura la lista browse sin re-fetch.
 */
export const useClientsPaginated = ({ attendant }: ClientsPaginatedParams) => {
  const dispatch = useAppDispatch()

  const [browse, setBrowse] = useState<PageState>(emptyPage)
  const [search, setSearch] = useState<PageState>(emptyPage)
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)

  // Guard contra respuestas fuera de orden: solo el request más reciente aplica.
  const requestRef = useRef(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const paramsRef = useRef({ attendant })
  paramsRef.current = { attendant }

  // Estado siempre fresco para los callbacks estables (onMenuOpen/onLoadMore).
  // react-select captura estos handlers dentro de su MenuList; sin el ref, un
  // closure viejo (loading=true / initialized=false) bloquearía la paginación.
  const stateRef = useRef({ browse, search, searchTerm, loading })
  stateRef.current = { browse, search, searchTerm, loading }

  useEffect(
    () => () => {
      clearTimeout(debounceRef.current)
      requestRef.current++
    },
    [],
  )

  const fetchPage = async (skip: number, term: string) => {
    const requestId = ++requestRef.current
    setLoading(true)
    try {
      const data: SimpleClient[] = await clientsService.getClientsSimple({
        skip,
        size: CLIENTS_PAGE_SIZE,
        search: term || undefined,
        attendant: paramsRef.current.attendant,
      })
      if (requestId !== requestRef.current) return
      const setPage = term ? setSearch : setBrowse
      setPage(prev => ({
        items: skip === 0 ? data : mergeById(prev.items, data),
        skip,
        // `>=` y no `===`: la página 0 puede traer los 2 clientes CA especiales
        // ADEMÁS de `size` normales (contrato ATL-323), quedando en size+2.
        // Con `===` eso daría hasMore=false y jamás paginaría.
        hasMore: data.length >= CLIENTS_PAGE_SIZE,
        initialized: true,
      }))
    } catch {
      if (requestId === requestRef.current)
        dispatch(showAlert(handlerError('No se pudieron cargar los clientes.')))
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
  }

  const onMenuOpen = () => {
    const { browse, loading } = stateRef.current
    if (!browse.initialized && !loading) void fetchPage(0, '')
  }

  const onLoadMore = () => {
    const { browse, search, searchTerm, loading } = stateRef.current
    const page = searchTerm ? search : browse
    if (page.initialized && page.hasMore && !loading) {
      void fetchPage(page.skip + CLIENTS_PAGE_SIZE, searchTerm)
    }
  }

  const onSearchChange = (input: string) => {
    clearTimeout(debounceRef.current)
    if (!input) {
      // Restaurar la lista browse acumulada y descartar búsquedas en vuelo.
      requestRef.current++
      setLoading(false)
      setSearchTerm('')
      setSearch(emptyPage)
      return
    }
    debounceRef.current = setTimeout(() => {
      setSearchTerm(input)
      void fetchPage(0, input)
    }, SEARCH_DEBOUNCE_MS)
  }

  return {
    clients: searchTerm ? search.items : browse.items,
    searching: searchTerm !== '',
    loading,
    onMenuOpen,
    onLoadMore,
    onSearchChange,
  }
}
